import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getPaymentHistory,
  subscribeToResize,
} from "../services/paymentService";
import { PaymentItem } from "../types/payment";
import { Filters } from "../components/HistoryFilter";
import { debounce } from "../utils/debounce";

function parseFiltersFromParams(params: URLSearchParams): Filters {
  return {
    creationTimeFrom: params.get("dateFrom") || undefined,
    creationTimeTo: params.get("dateTo") || undefined,
    statuses: params.getAll("statuses"),
    paymentId: params.get("paymentId") || undefined,
    merchantId: params.get("merchantId") || undefined,
    amountFrom: params.get("amountFrom")
      ? Number(params.get("amountFrom"))
      : undefined,
    amountTo: params.get("amountTo")
      ? Number(params.get("amountTo"))
      : undefined,
  };
}

function setParamsFromFilters(params: URLSearchParams, filters: Filters) {
  filters.creationTimeFrom
    ? params.set("dateFrom", filters.creationTimeFrom)
    : params.delete("dateFrom");
  filters.creationTimeTo
    ? params.set("dateTo", filters.creationTimeTo)
    : params.delete("dateTo");
  params.delete("statuses");
  filters.statuses.forEach((status) => params.append("statuses", status));
  filters.paymentId
    ? params.set("paymentId", filters.paymentId)
    : params.delete("paymentId");
  filters.merchantId
    ? params.set("merchantId", filters.merchantId)
    : params.delete("merchantId");
  filters.amountFrom !== undefined
    ? params.set("amountFrom", filters.amountFrom.toString())
    : params.delete("amountFrom");
  filters.amountTo !== undefined
    ? params.set("amountTo", filters.amountTo.toString())
    : params.delete("amountTo");
}

export const usePaymentHistory = () => {
  const [items, setItems] = useState<PaymentItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(0, Number(searchParams.get("page")) - 1 || 0);

  const filters = useMemo(
    () => parseFiltersFromParams(searchParams),
    [searchParams]
  );

  const fetchData = useCallback(
    async (pageToLoad: number, currentFilters: Filters = filters) => {
      try {
        setLoading(true);
        setErrorMessage(null);
        const response = await getPaymentHistory(
          pageToLoad,
          undefined,
          currentFilters
        );
        setItems(response.content);
        setTotalPages(response.page.totalPages);
      } catch (err: any) {
        setErrorMessage(
          err?.response?.status === 401
            ? "You are not logged in. Please log in again."
            : "Failed to load data."
        );
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    const noOtherFilters =
      !filters.creationTimeFrom &&
      !filters.creationTimeTo &&
      !filters.paymentId &&
      !filters.merchantId &&
      filters.amountFrom === undefined &&
      filters.amountTo === undefined;

    if (filters.statuses.length === 0 && noOtherFilters) {
      const defaultStatuses = ["IN_PROGRESS", "SUCCESS", "CANCELLED"];
      const newParams = new URLSearchParams(searchParams);
      defaultStatuses.forEach((status) => newParams.append("statuses", status));
      newParams.set("page", "1");
      setSearchParams(newParams);
    } else {
      fetchData(page);
    }
  }, [filters, page, searchParams, setSearchParams, fetchData]);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  useEffect(() => {
    if (isIOS) return;
    let prevHeight = window.innerHeight;
    let prevWidth = window.innerWidth;
    const handleResize = debounce(() => {
      if (
        window.innerHeight !== prevHeight ||
        window.innerWidth !== prevWidth
      ) {
        prevHeight = window.innerHeight;
        prevWidth = window.innerWidth;

        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("page", "1");
          return newParams;
        });
      }
    }, 1000);

    const unsubscribe = subscribeToResize(handleResize);

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams]);

  const setPage = (newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", (newPage + 1).toString());
    setSearchParams(newParams);
  };

  const applyFilters = (newFilters: Filters) => {
    if (
      (newFilters.amountFrom !== undefined && newFilters.amountFrom < 0) ||
      (newFilters.amountTo !== undefined && newFilters.amountTo < 0)
    ) {
      alert("Amount values cannot be negative.");
      return;
    }
    const newParams = new URLSearchParams();
    setParamsFromFilters(newParams, newFilters);
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  return {
    items,
    loading,
    page,
    totalPages,
    setPage,
    errorMessage,
    filters,
    applyFilters,
  };
};
